import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import useLogoUrl from "@/hooks/useLogoUrl";
import AdminSidebar, { Tab } from "./components/AdminSidebar";
import DashboardOverview from "./components/DashboardOverview";
import HomepageEditor from "./components/HomepageEditor";
import AboutEditor from "./components/AboutEditor";
import { ProductsEditor } from "./editors";
import PartnersEditor from "./components/PartnersEditor";
import MessagesManager from "./components/MessagesManager";
import SiteSettings from "./components/SiteSettings";
import ChangeLog from "./components/ChangeLog";
import UsersManager from "./components/UsersManager";
import { messages } from "@/mocks/messages";
import { userStore, AuthSession } from "@/utils/userStore";
import { siteData } from "@/store/siteData";
import NavigationManager from "./components/NavigationManager";

const VALID_TABS: Tab[] = ["dashboard", "homepage", "about", "products", "partners", "messages", "settings", "changelog", "users", "navigation"];

export default function KontrolPaneli() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const logoUrl = useLogoUrl();
  const [session, setSession] = useState<AuthSession | null>(() => userStore.getSession());
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const tabParam = searchParams.get("tab") as Tab | null;
    if (tabParam && VALID_TABS.includes(tabParam)) return tabParam;
    const saved = sessionStorage.getItem("yg_admin_tab") as Tab | null;
    return saved && VALID_TABS.includes(saved) ? saved : "dashboard";
  });
  const setAndSaveTab = (t: Tab) => {
    setActiveTab(t);
    sessionStorage.setItem("yg_admin_tab", t);
  };
  const [inspectorId, setInspectorId] = useState<number | null>(() => {
    const idParam = searchParams.get("id");
    return idParam ? Number(idParam) : null;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const tabParam = searchParams.get("tab") as Tab | null;
    const idParam = searchParams.get("id");
    if (tabParam && VALID_TABS.includes(tabParam)) setAndSaveTab(tabParam);
    setInspectorId(idParam ? Number(idParam) : null);
  }, [searchParams]);

  const newMessages = messages.filter((m) => m.status === "new").length;

  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async () => {
    setLoginError("");
    setLoginLoading(true);
    try {
      const newSession = await userStore.login(loginForm.username, loginForm.password);
      await siteData.load();
      setSession(newSession);
      // Support rolünde Mesajlar'a yönlendir
      if (newSession.role === "support") {
        setAndSaveTab("messages");
      }
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : "Kullanıcı adı veya şifre hatalı.");
      setLoginForm((p) => ({ ...p, password: "" }));
    }
    setLoginLoading(false);
  };

  const handleLogout = async () => {
    await userStore.logout();
    setSession(null);
    setLoginForm({ username: "", password: "" });
  };

  // ── Login Screen ────────────────────────────────────────────────────────────

  if (!session) {
    return (
      <div className="min-h-screen bg-[#0A1020] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img
              src={logoUrl}
              alt="Yenigül Yapı"
              className="h-14 w-auto object-contain mx-auto mb-6"
            />
            <h1 className="text-white text-xl font-bold">Yönetim Paneli</h1>
            <p className="text-[#7B8FAF] text-sm mt-1">Giriş yapmak için bilgilerinizi girin</p>
          </div>

          <div className={`bg-[#141E35] border rounded-2xl p-8 space-y-4 ${loginError ? "border-red-500/40" : "border-[#2B3654]"}`}>
            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Kullanıcı Adı</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#3B82F6]">
                  <i className="ri-user-3-line text-sm"></i>
                </div>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => { setLoginForm((p) => ({ ...p, username: e.target.value })); setLoginError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleLogin(); }}
                  placeholder="kullanici_adi"
                  className={`w-full bg-[#0F1729] border text-white text-sm rounded-lg pl-9 pr-4 py-3 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398] ${loginError ? "border-red-500/60" : "border-[#2B3654]"}`}
                />
              </div>
            </div>

            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Şifre</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#3B82F6]">
                  <i className="ri-lock-line text-sm"></i>
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={loginForm.password}
                  onChange={(e) => { setLoginForm((p) => ({ ...p, password: e.target.value })); setLoginError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleLogin(); }}
                  placeholder="••••••••"
                  className={`w-full bg-[#0F1729] border text-white text-sm rounded-lg pl-9 pr-10 py-3 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398] ${loginError ? "border-red-500/60" : "border-[#2B3654]"}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3B82F6] cursor-pointer w-4 h-4 flex items-center justify-center"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            {loginError && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-3 py-2.5 rounded-lg">
                <i className="ri-error-warning-line text-sm flex-shrink-0"></i>
                {loginError}
              </div>
            )}

            <button
              onClick={handleLogin}
              disabled={loginLoading}
              className="w-full bg-[#3B82F6] hover:bg-[#3d4e6a] text-white font-medium py-3 rounded-lg transition-colors cursor-pointer text-sm whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {loginLoading && <i className="ri-loader-4-line animate-spin"></i>}
              {loginLoading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Panel ──────────────────────────────────────────────────────────────

  const tabTitle: Record<Tab, string> = {
    dashboard: "Genel Bakış",
    homepage: "Ana Sayfa İçerikleri",
    about: "Hakkımızda Sayfası",
    products: "Ürünlerimiz",
    partners: "Stratejik Ortaklar",
    navigation: "Sayfa Yönetimi",
    messages: "Mesajlar",
    settings: "Site Ayarları",
    changelog: "Değişiklik Günlüğü",
    users: "Kullanıcılar & Güvenlik",
  };

  return (
    <div className="min-h-screen bg-[#0A1020] flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* Sidebar — mobilde fixed, desktop'ta sticky */}
      <div className={`fixed lg:sticky lg:top-0 lg:self-start top-0 left-0 h-screen z-50 transition-transform duration-300 lg:translate-x-0 flex-shrink-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <AdminSidebar
          activeTab={activeTab}
          onTabChange={(tab) => { setAndSaveTab(tab); setSidebarOpen(false); }}
          newMessages={newMessages}
          userRole={session.role}
        />
      </div>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#0F1729] border-b border-[#2B3654] px-5 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden w-9 h-9 flex items-center justify-center text-[#7B8FAF] hover:text-white transition-colors cursor-pointer"
              onClick={() => setSidebarOpen(true)}
            >
              <i className="ri-menu-3-line text-xl"></i>
            </button>
            <div className="hidden sm:flex items-center gap-2 text-[#5E7398] text-xs">
              <i className="ri-map-pin-2-line text-[#3B82F6]"></i>
              <span>{tabTitle[activeTab]}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {session.role !== "support" && (
              <button
                onClick={() => navigate("/site-inspector")}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-medium rounded-lg cursor-pointer hover:bg-emerald-500/25 transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-edit-box-line text-sm"></i>
                </div>
                <span>Görsel Editör</span>
              </button>
            )}

            <p className="text-[#465886] text-xs hidden lg:block">Değişiklikler anında kaydedilir</p>

            {newMessages > 0 && (
              <button
                onClick={() => setAndSaveTab("messages")}
                className="relative w-9 h-9 flex items-center justify-center text-[#7B8FAF] hover:text-white cursor-pointer"
              >
                <i className="ri-notification-3-line text-xl"></i>
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#3B82F6] rounded-full text-white text-xs flex items-center justify-center font-bold">{newMessages}</span>
              </button>
            )}

            {session.role === "admin" && (
              <button
                onClick={() => setAndSaveTab("changelog")}
                className={`relative w-9 h-9 flex items-center justify-center cursor-pointer transition-colors ${activeTab === "changelog" ? "text-[#3B82F6]" : "text-[#7B8FAF] hover:text-white"}`}
                title="Değişiklik Günlüğü"
              >
                <i className="ri-history-line text-xl"></i>
              </button>
            )}

            <div className="flex items-center gap-2 pl-2 border-l border-[#2B3654] ml-1">
              <button
                onClick={() => setAndSaveTab("users")}
                className="flex items-center gap-2 text-[#7B8FAF] hover:text-white text-xs transition-colors cursor-pointer px-2 py-1.5 rounded-lg hover:bg-white/5 whitespace-nowrap"
                title="Kullanıcı Ayarları"
              >
                <div className="w-6 h-6 rounded-full bg-[#3B82F6]/40 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {session.displayName.charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:inline">{session.displayName}</span>
                {session.role === "admin" && (
                  <span className="hidden md:inline text-amber-400/70 text-xs">Admin</span>
                )}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-[#7B8FAF] hover:text-white text-sm transition-colors cursor-pointer p-1.5 rounded-lg hover:bg-white/5"
                title="Çıkış Yap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-logout-box-r-line text-sm"></i>
                </div>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-5 lg:p-8 overflow-y-auto">
          {activeTab === "dashboard" && <DashboardOverview onNavigate={setAndSaveTab} />}
          {activeTab === "homepage" && <HomepageEditor />}
          {activeTab === "about" && <AboutEditor />}
          {activeTab === "products" && <ProductsEditor />}
          {activeTab === "partners" && <PartnersEditor initialId={inspectorId} />}
          {activeTab === "messages" && <MessagesManager />}
          {activeTab === "settings" && <SiteSettings userRole={session.role} />}
          {activeTab === "changelog" && <ChangeLog />}
          {activeTab === "navigation" && <NavigationManager />}
          {activeTab === "users" && (
            <UsersManager
              currentUserId={session.userId}
              currentRole={session.role}
            />
          )}
        </main>
      </div>
    </div>
  );
}
